<template>
  <div id="app">
    <notifications position="top left" :duration="5000" animation-type="velocity" />

    <component :is="layout" v-if="authReady" />

    <div class="loading-bar" v-if="!authReady">
      <base-logo-wave-anim />
    </div>
    <vue-progress-bar></vue-progress-bar>

    <div class="refresh-app-wrapper" v-if="updateExists">
      <app-update-popup @refresh="refreshApp" class="refresh-app d-inline-flex flex-column align-items-center"></app-update-popup>
    </div>

    <help v-if="authCheck" />

    <install-app-prompt />

    <base-popup
      class="app-base-popup"
      v-show="popup_visibility"
      @close="close_subscribe_popup"
    >
      <base-logo-wave class="mb-1 mt-3 my-sm-5"/>

      <h4 v-if="free_subscription" class="mt-4 text-center" v-html="current_user.free_subscribe_popup_text"></h4>
      <h4 v-else class="mt-4 text-center" v-html="current_user.subscribe_popup_text"></h4>

      <div v-if="free_subscription" class="w-75 my-3 my-sm-4 d-flex justify-content-center align-self-center">
        <subscription-plan-card :subscription_plan="free_subscription" />
      </div>

      <div v-else>
        <base-button
          class="btn-sm my-4"
          @click.native="subscribe">
          {{ $t('popup.subscribe_button') }}
        </base-button>
      </div>

      <small class="mt-2 mt-sm-4 font-weight-light text-center" v-html="current_user.subscribe_popup_sub_text"></small>
    </base-popup>

    <description-popup />
  </div>
</template>

<script>
// layouts
import RootLayout from './views/layouts/Root'
import AuthLayout from './views/layouts/Auth'
import ElliotWaveSchoolLayout from './views/layouts/ElliotWaveSchool'
import Help from './components/Help.vue'
import InstallAppPrompt from './components/InstallAppPrompt.vue'
import BaseLogoWaveAnim from './components/BaseLogoWaveAnim.vue'
import AppUpdatePopup from './components/AppUpdatePopup.vue'
import { mapState, mapActions } from 'vuex'
import BasePopup from './components/BasePopup.vue'
import BaseButton from './components/BaseButton.vue'
import Vue from 'vue'
import BaseLogoWave from './components/BaseLogoWave.vue'
import SubscriptionPlanCard from './components/SubscriptionPlanCard.vue'
import DescriptionPopup from './components/DescriptionPopup.vue'

export default {
  data () {
    return {
      refreshing: false,
      registration: null,
      updateExists: false,
      subscribeTimeoutActive: false,
      unrestrictedPaths: [
        'profile.payment_and_subscription',
        'profile.settings',
        'profile.account',
        'elliot-wave-school'
      ],
      free_subscription: {}
    }
  },
  components: {
    DescriptionPopup,
    SubscriptionPlanCard,
    BaseLogoWave,
    BaseButton,
    BasePopup,
    'default-layout': RootLayout,
    'profile-layout': RootLayout,
    'auth-layout': AuthLayout,
    'elliot-wave-school-layout': ElliotWaveSchoolLayout,
    Help,
    BaseLogoWaveAnim,
    AppUpdatePopup,
    InstallAppPrompt
  },
  computed: {
    layout () {
      return (this.$route.meta.layout || 'default') + '-layout'
    },
    authReady () {
      return this.$auth.ready()
    },
    authCheck () {
      return this.$auth.check()
    },
    currentPathName () {
      return this.$route.name
    },
    ...mapState({
      wisepops: (state) => state.wisepops.wisepops,
      blocked: (state) => state.blocked.blocked.blocked,
      current_user: (state) => state.profile.account.current_user,
      popup_visibility: (state) => state.subscribe_popup.popup_visibility,
      subscription_plans: (state) => state.subscription_plans.subscription_plans,
      published_subscription_plans: (state) => state.subscription_plans.published_subscription_plans
    }),
    active_market () {
      return this.$route.matched[0].meta.market
    }
  },
  mounted () {
    this.$auth.load().then(() => {
      if (this.$auth.check()) {
        this.$store.dispatch('profile/account/loadCurrentUser').then(() => {
          if (this.current_user.blocked) {
            this.$router.push({ name: 'blocked' })
          }
        })

        this.$store.dispatch('wisepops/loadWisepops').then(() => {
          this.wisepops.forEach((wisepop) => {
            this.$wisepops('event', wisepop.custom_event_name)
          })
        })
      }
    })

    this.$store.dispatch('blocked/checkBlocked').then(() => {
      if (this.blocked) {
        this.$router.push({ name: 'blocked' })
      }
    })

    this.$store.dispatch('subscription_plans/loadSubscriptionPlans').then(() => {
      this.set_published_subscriptions()
      this.check_free_subscription_availability()
    })

    this.$Progress.finish()
  },
  created () {
    this.$Progress.start()

    document.addEventListener('swUpdated', this.showRefreshUI, { once: true })
    this.checkForWaitingServiceWorker()

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (this.refreshing) return

        this.refreshing = true
        window.location.reload()
      })
    }
  },
  watch: {
    currentPathName: function (pathName) {
      this.check_free_subscription_availability()

      if (!this.unrestrictedPaths.includes(this.currentPathName) && !this.subscribeTimeoutActive) {
        this.start_subscriptions_timeout()
      }
    }
  },
  methods: {
    ...mapActions({
      open_popup: 'subscribe_popup/open_popup',
      close_popup: 'subscribe_popup/close_popup',
      set_published_subscription_plans: 'subscription_plans/set_published_subscription_plans'
    }),
    check_for_subscription () {
      if (this.current_user.open_door) {
        return true
      }

      return this.current_user.subscriptions.length > 0
    },
    start_subscriptions_timeout () {
      this.subscribeTimeoutActive = true

      setTimeout(() => {
        if (!this.check_for_subscription() && !this.popup_visibility && Vue.auth.user() && !this.unrestrictedPaths.includes(this.currentPathName)) {
          this.open_subscribe_popup()
        }

        this.subscribeTimeoutActive = false
      }, 60000)
    },
    showRefreshUI (e) {
      this.registration = e.detail
      this.updateExists = true
    },
    checkForWaitingServiceWorker () {
      if (!navigator.serviceWorker || !navigator.serviceWorker.getRegistration) {
        return
      }

      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && registration.waiting) {
          this.registration = registration
          this.updateExists = true
        }
      })
    },
    async refreshApp () {
      this.updateExists = false

      if (!this.registration || !this.registration.waiting) {
        return
      }

      localStorage.clear()

      if (window.caches) {
        const cacheKeys = await window.caches.keys()
        await Promise.all(cacheKeys.map((key) => window.caches.delete(key)))
      }

      this.registration.waiting.postMessage('skipWaiting')
    },
    subscribe () {
      this.close_popup()
      this.$router.push({ name: 'profile.payment_and_subscription' })
    },
    open_subscribe_popup () {
      this.open_popup()
    },
    close_subscribe_popup () {
      this.close_popup()

      if (!this.subscribeTimeoutActive) {
        this.start_subscriptions_timeout()
      }
    },
    set_published_subscriptions () {
      const published_subscriptions = this.subscription_plans.filter(
        sp => (sp.published === true || this.current_user.subscriptions.some(cus => cus.recurly_plan.id === sp.id))
      ).sort((a, b) => a.order - b.order)

      this.set_published_subscription_plans(published_subscriptions)
    },
    check_free_subscription_availability () {
      this.free_subscription = this.published_subscription_plans.find(sp => sp.plan_type === 'free' &&
        sp.markets.some(m => m.slug === this.active_market) && !this.current_user.subscriptions.some(cus => cus.custom_plan_id === sp.id)) || null
    }
  }
}
</script>

<style scoped lang="scss">
.loading-bar {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: var(--white-black);
}

.refresh-app-wrapper {
  position: absolute;
  background-color: var(--shadow-black);
  width: 100%;
  height: 100%;
  z-index: 998;
  top: 0;
  left: 0;
}

.refresh-app {
  position: fixed;
  z-index: 999;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 20px;

  background-color: var(--white-windows-blue);
  border-color: var(--light-peach);

  width: 480px;
  padding: 60px 50px;
  gap: 60px;

  @media (max-width: 575.98px) {
    width: 100%;
    height: 100%;
  }
}

::v-deep .app-base-popup {
  .popup-wrapper {
    background-color: var(--shadow-gray);
    height: 100%;

    h4 {
      font-size: 16px;
      font-weight: 400;
      color: var(--dark-grey-white);
      margin-bottom: 14px;
      margin-left: -5px;
      margin-top: 30px;
    }

    .popup-content {
      text-align: center;
      padding: 36px 20px 20px 20px;
      max-width: 400px;
      margin: 0 auto;
      top: 50%;
      transform: translateY(-50%);
      color: var(--cool-grey);
    }
  }
}

@media (max-width: 575.98px) {
  ::v-deep  .popup-wrapper{
    padding: 0 15px;
  }
}

::v-deep .plan {
  width: 90%;
  border: solid 1px var(--pale-grey-battleship-grey-three);
  padding: 38px;
  position: relative;
  animation: fadeIn ease 1s;
  height: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;

  h5 {
    display: inline-block;
    word-break: break-word;
    font-size: 25px;
    font-weight: 300;
    color: var(--black-white);
    margin-bottom: 25px;
  }

  p {
    font-size: 15px;
    font-weight: 300;
    font-family: 'Athelas', arial, sans-serif;
    line-height: 1.53;
    margin-bottom: 5px;
  }

  a {
    display: block;
    font-size: 13px;
    color: var(--blueberry);
    font-weight: 300;

    &:not([href]):not([tabindex]) {
      cursor: pointer;
    }
  }

  .btn-primary {
    color: var(--white);
    font-weight: 300;
    bottom: 0;
  }

  .validation {
    p {
      margin-top: 35px;
      margin-bottom: 20px;
    }

    #cancel-subscription {
      color: var(--dark-coral);
      font-size: 12px;
      background-color: var(--very-light-light-black);

      &:hover {
        opacity: .9;
      }
    }
  }

  .price {
    font-family: 'Montserrat', 'Arial', sans-serif;
    font-weight: 300;
    margin-bottom: 20px;
    margin-top: 35px;
    font-size: 24px;
    line-height: 15px;

    span {
      font-size: 14px;
    }

    small {
      font-size: 14px;
      color: var(--cloudy-blue);
      font-family: 'Heebo', arial, sans-serif;
    }
  }

  .image-wrapper {
    position: relative;
    display: inline-block;

    img {
      margin-bottom: 60px;
      width: 50px;

      &:not(:first-child) {
        position: absolute;
        left: 25px;
        top: 25px;
      }

      &:nth-child(3) {
        left: 50px;
        top: 50px;
      }
    }
  }

  .btn {
    max-width: 150px;
    min-width: 150px;
  }

  .discount-badge {
     position: absolute;
     top: 8px;
     right: 0;

     span {
       font-size: 20px;
       color: var(--white);
       background-color: var(--tealish-two);
       padding: 2px 5px;
     }
  }

  .cancel-subscription {
    margin-top: 55px;
    padding-bottom: 140px;

    h4 {
      margin-left: 0;
    }

    p {
      font-family: 'Athelas', arial, sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 1.63;
      letter-spacing: 0.1px;
      margin-bottom: 0;
      min-height: inherit;
      color: var(--charcoal-grey-white);
    }

    a {
      @include basic-transition;
      cursor: pointer;
      text-decoration: underline;

      &:hover {
        opacity: .8;
      }
    }
  }

  .discount-strikethrough {
    position: relative;
    font-size: 13px;
    font-weight: 300;
    color: var(--cloudy-blue);
    text-decoration: line-through;
  }
}
</style>
